import { WeatherConfig } from "../components/Graphs/Common/weatherConfig";
import { WeatherResponse, WeatherForecastResponse, HourlyWeatherData } from "./Types";

export interface WeatherTooltipItem {
  key: string;
  name: string;
  value: string;
  color: string;
}

const wmoDescriptions: Record<number, string> = {
  0: "Clear sky",
  1: "Mainly clear",
  2: "Partly cloudy",
  3: "Overcast",

  45: "Fog",
  48: "Depositing rime fog",

  51: "Light drizzle",
  53: "Moderate drizzle",
  55: "Dense drizzle",

  56: "Light freezing drizzle",
  57: "Dense freezing drizzle",

  61: "Slight rain",
  63: "Moderate rain",
  65: "Heavy rain",

  66: "Light freezing rain",
  67: "Heavy freezing rain",

  71: "Slight snowfall",
  73: "Moderate snowfall",
  75: "Heavy snowfall",

  77: "Snow grains",

  80: "Slight rain showers",
  81: "Moderate rain showers",
  82: "Violent rain showers",

  85: "Slight snow showers",
  86: "Heavy snow showers",

  95: "Thunderstorm",
  96: "Thunderstorm with slight hail",
  99: "Thunderstorm with heavy hail",
};

export const getWeatherDescriptionFromWMO = (weatherCode: number): string => {
  return wmoDescriptions[weatherCode] || "Unknown weather condition";
};

const getClosestWeatherData = (
  weatherResponse: WeatherForecastResponse,
  targetUnixTime: number,
): HourlyWeatherData | null => {
  if (!weatherResponse?.hourly || !weatherResponse?.hourly?.length) {
    return null;
  }

  const TWO_HOURS_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // Find the closest time point by comparing timestamps
  let closestData = weatherResponse.hourly[0];
  let minTimeDiff = Math.abs(new Date(closestData.time).getTime() - targetUnixTime);

  for (const dataPoint of weatherResponse.hourly) {
    const timeDiff = Math.abs(new Date(dataPoint.time).getTime() - targetUnixTime);
    if (timeDiff < minTimeDiff) {
      minTimeDiff = timeDiff;
      closestData = dataPoint;
    }
  }

  // Return null if the closest data point is more than 2 hours away
  if (minTimeDiff > TWO_HOURS_MS) {
    return null;
  }

  return closestData;
};

export const generateGetWeatherTooltipItems = (
  weatherConfig: Record<string, WeatherConfig>,
  weatherResponse: WeatherForecastResponse,
) => {
  return (payload: any): Record<string, WeatherTooltipItem> => {
    if (!payload || payload.length === 0) return {};

    const payloadUnixTime = payload[0].payload.unixTime;
    const weatherData = getClosestWeatherData(weatherResponse, payloadUnixTime);

    if (!weatherData) return {};

    const items: Record<string, WeatherTooltipItem> = {
      temperature: {
        key: "Temperature",
        name: "Temperature",
        value: `${weatherData.temperature_2m}°F`,
        color: weatherConfig.temperature_2m.color,
      },
      cloudCover: {
        key: "Cloud Cover",
        name: "Cloud Cover",
        value: `${weatherData.cloud_cover}%`,
        color: weatherConfig.cloud_cover.color,
      },
      rain: {
        key: "Rain",
        name: "Rain",
        value: `${weatherData.rain}in`,
        color: weatherConfig.rain.color,
      },
    };

    return items;
  };
};
